// Server settings → Emoji and → Stickers (owners and admins): upload, rename, delete, and (the owner, from Emoji)
// import everything from a Discord server. The lists come live from the room, so changes made here, by another admin,
// or by an import show up at once everywhere.
import { useEffect, useRef, useState } from "react";
import {
  cleanEmojiName,
  cleanStickerName,
  MAX_EMOJI_BYTES,
  MAX_SERVER_EMOJI,
  MAX_SERVER_STICKERS,
  MAX_STICKER_BYTES,
  stickerUrl,
  type CustomEmoji,
  type DiscordGuild,
  type DiscordImportEvent,
  type Member,
  type ServerSummary,
  type Sticker,
} from "@bloom/shared";
import { api, ApiError } from "../api.ts";
import { CustomEmojiView, useServerEmoji } from "../customEmoji.tsx";
import { uploadCustom } from "../customUpload.ts";
import { Confirm } from "./Confirm.tsx";
import { initials } from "./ui.tsx";
import "./emoji-admin.css";

type Kind = "emoji" | "sticker";
type Item = CustomEmoji | Sticker;
const path = (serverId: string, kind: Kind) => `/servers/${serverId}/${kind === "emoji" ? "emoji" : "stickers"}`;

export function CustomSettings({ server, kind, members }: { server: ServerSummary; kind: Kind; members: Member[] }) {
  const { emojis, stickers, serverId } = useServerEmoji();
  const list: Item[] = kind === "emoji" ? emojis : stickers;
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<Item | null>(null);
  const [importing, setImporting] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const max = kind === "emoji" ? MAX_SERVER_EMOJI : MAX_SERVER_STICKERS;
  const maxKb = (kind === "emoji" ? MAX_EMOJI_BYTES : MAX_STICKER_BYTES) / 1024;
  const names = new Map(members.map((m) => [m.id, m.name]));
  const what = kind === "emoji" ? "emoji" : "stickers";

  const addFiles = async (files: File[]) => {
    setErrors([]);
    const problems: string[] = [];
    for (const [i, file] of files.entries()) {
      setBusy(`Uploading ${i + 1} of ${files.length}…`);
      const name = kind === "emoji" ? cleanEmojiName(file.name) : cleanStickerName(file.name);
      try {
        await uploadCustom(server.id, kind, file, name.length >= 2 ? name : `${kind}_${Date.now() % 100000}`);
      } catch (e) {
        problems.push(`${file.name}: ${(e as Error).message}`);
      }
    }
    setBusy(null);
    setErrors(problems);
  };

  const rename = async (item: Item, name: string) => {
    setErrors([]);
    try {
      await api(`${path(server.id, kind)}/${item.id}`, { method: "PATCH", body: { name } });
      setEditing(null);
    } catch (e) {
      setErrors([(e as Error).message]);
    }
  };

  const remove = async (item: Item): Promise<string | null> => {
    setErrors([]);
    try {
      await api(`${path(server.id, kind)}/${item.id}`, { method: "DELETE" });
      return null;
    } catch (e) {
      return (e as Error).message;
    }
  };

  return (
    <>
      <p className="setting-help" style={{ margin: "0 0 10px" }}>
        {kind === "emoji"
          ? `Everyone here can use these in messages and reactions: type :name:. PNG, GIF or WebP (animated is fine), up to ${maxKb} KB.`
          : `Everyone here can send these from the sticker button in the message box. PNG, APNG, GIF or WebP, up to ${maxKb} KB.`}
      </p>
      <div className="ce-toolbar">
        <button type="button" className="btn btn-small btn-primary" disabled={!!busy || list.length >= max} onClick={() => input.current?.click()}>
          Upload {kind === "emoji" ? "emoji" : "sticker"}
        </button>
        {kind === "emoji" && server.role === "owner" && (
          <button type="button" className="btn btn-small" onClick={() => setImporting(true)}>
            Import from Discord
          </button>
        )}
        <span className="muted ce-count">{list.length} of {max}</span>
        <input
          ref={input}
          type="file"
          hidden
          multiple
          accept={kind === "emoji" ? "image/png,image/gif,image/webp" : "image/png,image/apng,image/gif,image/webp"}
          aria-label={`Upload ${what}`}
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = "";
            if (files.length) void addFiles(files);
          }}
        />
      </div>
      {busy && <p className="muted" role="status" style={{ fontSize: 13 }}>{busy}</p>}
      {errors.length > 0 && (
        <div className="error" role="alert">
          {errors.map((e) => <div key={e}>{e}</div>)}
        </div>
      )}
      {list.length === 0 ? (
        <p className="muted ce-empty">No {what} yet.</p>
      ) : (
        <ul className={`ss-list ${kind === "emoji" ? "ce-list" : "ce-list ce-stickers"}`} aria-label={kind === "emoji" ? "Server emoji" : "Server stickers"}>
          {list.map((item) => (
            <li key={item.id} className="ce-row">
              <span className="ce-pic">
                {kind === "emoji" ? <CustomEmojiView id={item.id} name={item.name} className="cemoji cemoji-admin" /> : <img src={stickerUrl(serverId, item.id)} alt="" className="ce-sticker" loading="lazy" />}
              </span>
              {editing?.id === item.id ? (
                <form
                  className="ce-edit"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void rename(item, editing.name);
                  }}
                >
                  <input
                    className="input"
                    autoFocus
                    aria-label="New name"
                    maxLength={kind === "emoji" ? 32 : 30}
                    value={editing.name}
                    onChange={(e) => setEditing({ id: item.id, name: kind === "emoji" ? e.target.value.replace(/[^A-Za-z0-9_]/g, "_") : e.target.value })}
                    onKeyDown={(e) => e.key === "Escape" && (e.stopPropagation(), setEditing(null))}
                  />
                  <button className="btn btn-small btn-primary" type="submit">Save</button>
                  <button className="btn btn-small" type="button" onClick={() => setEditing(null)}>Cancel</button>
                </form>
              ) : (
                <div className="ce-text">
                  <div className="ce-name">{kind === "emoji" ? `:${item.name}:` : item.name}</div>
                  <div className="muted ce-sub">
                    {"animated" in item && item.animated ? "Animated · " : "format" in item ? `${item.format.toUpperCase()} · ` : ""}
                    added by {names.get(item.createdBy) ?? "someone"}
                  </div>
                </div>
              )}
              {editing?.id !== item.id && (
                <div className="ce-actions">
                  <button type="button" className="btn btn-small" onClick={() => setEditing({ id: item.id, name: item.name })} aria-label={`Rename ${item.name}`}>Rename</button>
                  <button type="button" className="btn btn-small ce-delete" onClick={() => setDeleting(item)} aria-label={`Delete ${item.name}`}>Delete</button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {deleting && (
        <Confirm
          title={`Delete ${kind === "emoji" ? `:${deleting.name}:` : `“${deleting.name}”`}?`}
          confirmLabel="Delete"
          danger
          onConfirm={() => remove(deleting)}
          onClose={() => setDeleting(null)}
        >
          {kind === "emoji" ? "Messages that use it will show its name instead." : "Messages that sent it will show its name instead."}
        </Confirm>
      )}
      {importing && <DiscordImport server={server} onClose={() => setImporting(false)} />}
    </>
  );
}

type Step =
  | { s: "token" }
  | { s: "guilds"; guilds: DiscordGuild[] }
  | { s: "running"; guild: DiscordGuild; total: number; done: number; last?: string }
  | { s: "done"; guild: DiscordGuild; result: Extract<DiscordImportEvent, { t: "done" }> };

/**
 * Import from Discord: paste a bot token, pick one of the bot's servers, watch it copy. The token lives only in this
 * dialog's memory and goes to Bloom in request bodies; Bloom never keeps it.
 */
function DiscordImport({ server, onClose }: { server: ServerSummary; onClose: () => void }) {
  const [token, setToken] = useState("");
  const [step, setStep] = useState<Step>({ s: "token" });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const listGuilds = async () => {
    setError(null);
    setLoading(true);
    try {
      const guilds = await api<DiscordGuild[]>(`/servers/${server.id}/discord/guilds`, { body: { token } });
      setStep({ s: "guilds", guilds });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const run = async (guild: DiscordGuild) => {
    setError(null);
    setStep({ s: "running", guild, total: 0, done: 0 });
    try {
      const res = await fetch(`/api/servers/${server.id}/discord/import`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, guildId: guild.id }),
      });
      if (!res.ok || !res.body) {
        const j = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(j?.message ?? `The import didn't start (${res.status}).`);
      }
      const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
      let buf = "";
      let finished = false;
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += value;
        let nl: number;
        while ((nl = buf.indexOf("\n")) >= 0) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!line) continue;
          const e = JSON.parse(line) as DiscordImportEvent;
          if (e.t === "start") setStep({ s: "running", guild, total: e.emojis + e.stickers, done: 0 });
          else if (e.t === "item") setStep({ s: "running", guild, total: e.total, done: e.done, last: `${e.kind === "emoji" ? `:${e.name}:` : e.name} · ${e.status}` });
          else if (e.t === "done") {
            finished = true;
            setStep({ s: "done", guild, result: e });
          } else if (e.t === "error") throw new Error(e.message);
        }
      }
      if (!finished) throw new Error("The import stopped partway. What was imported so far is kept.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setToken(""); // done with it
    }
  };

  const running = step.s === "running";
  // Esc closes the dialog (not the settings page behind it), except while an import is running.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      if (!running) onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [running]);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && !running && onClose()}>
      <div className="modal ce-import" role="dialog" aria-modal="true" aria-labelledby="ce-import-title">
        <h2 id="ce-import-title">Import from Discord</h2>
        {step.s === "token" && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void listGuilds();
            }}
          >
            <p className="setting-help">
              Copies a Discord server's emoji and stickers here, with the same names. You need a bot that's in that server: in the Discord Developer Portal open your app → Bot → Reset Token, and paste
              the token below. Bloom uses it for this import only and never stores it.
            </p>
            <label className="ce-label" htmlFor="ce-token">Bot token</label>
            <input id="ce-token" className="input" type="password" autoComplete="off" spellCheck={false} value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste the bot token" autoFocus />
            {error && <p className="error" role="alert">{error}</p>}
            <div className="confirm-actions" style={{ marginTop: 14 }}>
              <button type="button" className="btn btn-small" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-small btn-primary" disabled={!token.trim() || loading}>{loading ? "Checking…" : "Next"}</button>
            </div>
          </form>
        )}
        {step.s === "guilds" && (
          <>
            <p className="setting-help">{step.guilds.length ? "Which Discord server should we copy from?" : "That bot isn't in any Discord server yet. Invite it to yours first, then try again."}</p>
            <ul className="ss-list ce-guilds" aria-label="Discord servers">
              {step.guilds.map((g) => (
                <li key={g.id}>
                  <button type="button" className="ce-guild" onClick={() => void run(g)}>
                    {g.icon ? <img src={g.icon} alt="" width={32} height={32} /> : <span className="ce-guild-initials" aria-hidden="true">{initials(g.name)}</span>}
                    <span>{g.name}</span>
                  </button>
                </li>
              ))}
            </ul>
            {error && <p className="error" role="alert">{error}</p>}
            <div className="confirm-actions" style={{ marginTop: 14 }}>
              <button type="button" className="btn btn-small" onClick={() => setStep({ s: "token" })}>Back</button>
            </div>
          </>
        )}
        {step.s === "running" && (
          <>
            <p className="setting-help">Copying from <strong>{step.guild.name}</strong>…</p>
            <div className="ss-bar" role="progressbar" aria-label="Import progress" aria-valuemin={0} aria-valuemax={step.total || 1} aria-valuenow={step.done}>
              <span style={{ width: `${step.total ? Math.round((100 * step.done) / step.total) : 0}%` }} />
            </div>
            <p className="muted" role="status" style={{ fontSize: 13 }}>
              {step.done} of {step.total || "…"}
              {step.last ? ` · ${step.last}` : ""}
            </p>
            {error && (
              <>
                <p className="error" role="alert">{error}</p>
                <div className="confirm-actions"><button type="button" className="btn btn-small" onClick={onClose}>Close</button></div>
              </>
            )}
          </>
        )}
        {step.s === "done" && (
          <>
            <p className="ce-summary" role="status">
              Imported {step.result.imported} from <strong>{step.guild.name}</strong>
              {step.result.skipped.length ? ` · skipped ${step.result.skipped.length}` : ""}
              {step.result.failed.length ? ` · failed ${step.result.failed.length}` : ""}.
            </p>
            {[...step.result.skipped.map((x) => ({ ...x, how: "Skipped" })), ...step.result.failed.map((x) => ({ ...x, how: "Failed" }))].length > 0 && (
              <ul className="ce-report" aria-label="Not imported">
                {step.result.skipped.map((x, i) => (
                  <li key={`s${i}`}><strong>{x.kind === "emoji" ? `:${x.name}:` : x.name}</strong> <span className="muted">skipped: {x.reason}</span></li>
                ))}
                {step.result.failed.map((x, i) => (
                  <li key={`f${i}`}><strong>{x.kind === "emoji" ? `:${x.name}:` : x.name}</strong> <span className="error">failed: {x.reason}</span></li>
                ))}
              </ul>
            )}
            <div className="confirm-actions" style={{ marginTop: 14 }}>
              <button type="button" className="btn btn-small btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
